import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router";
import { css, html } from "react-strict-dom";
import { Spinner, ThemeProvider } from "@duro-app/ui";
import "@duro-app/ui/dist/index.css";
// Build-time StyleX rules (see stylex.css) — after the DS CSS, so the
// design system declares the layer order.
import "./stylex.css";
// Restore a light page surface — the DS CSS is dark-first (hard-codes a dark
// body); must be imported AFTER it. See app-surface.css.
import "./app-surface.css";

import { LocalEditor, localEditorLoader } from "./routes/LocalEditor.js";
import { SiteDetail, siteDetailLoader } from "./routes/SiteDetail.js";
import { registerDuroPrimitives } from "./theme/registerDuroPrimitives.js";
import { registerWidgetSettingsForms } from "./editor/widgetSettings/register.js";
import { PREVIEW_ROOT, PREVIEW_SW_URL } from "./preview/protocol.js";
import { DesignSystemScope } from "./theme/designSystemContext.js";
import { duroModule } from "./theme/duroModule.js";

// Make the editor render real Duro components for the themed primitives
// (button/heading/card) via its catalog injection seam.
registerDuroPrimitives();
registerWidgetSettingsForms();

// Shown while the matched route's loader runs on the initial client render.
// Without a HydrateFallback, a data router renders nothing during initial
// hydration and React Router logs a warning.
const styles = css.create({
  hydrateFallback: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
  },
});

function HydrateFallback() {
  return (
    <html.div style={styles.hydrateFallback}>
      <Spinner size="lg" label="Loading…" />
    </html.div>
  );
}

const router = createBrowserRouter([
  {
    // Pathless layout route: its HydrateFallback covers the initial-hydration
    // window for every child while their loaders run (no element → Outlet).
    HydrateFallback,
    children: [
      {
        // The builder is free: `/` opens straight into a local draft editor
        // with no login. Login is only required to publish online (see
        // PublishDialog).
        path: "/",
        loader: localEditorLoader,
        element: <LocalEditor />,
      },
      {
        path: "/sites/:id",
        loader: siteDetailLoader,
        element: <SiteDetail />,
      },
    ],
  },
]);

async function maybeStartMsw() {
  // import.meta.env.DEV is inlined as `false` by Vite's prod build —
  // the entire dynamic-import chain (and msw itself) tree-shakes out
  // of dist/. Verified by checking dist/assets/index-*.js for "msw"
  // strings; none in prod.
  if (!import.meta.env.DEV) return;
  // A harness spec that fakes /api itself (shared between browser contexts,
  // i.e. "devices") turns the mocks off: MSW would otherwise answer in-page,
  // out of reach of the spec's request interception.
  try {
    if (localStorage.getItem("wb.dev.msw") === "off") return;
  } catch {
    // Storage unavailable: keep the mocks.
  }
  const { worker } = await import("./mocks/browser.js");
  await worker.start({
    onUnhandledRequest: "bypass",
    serviceWorker: { url: "/mockServiceWorker.js" },
  });
  console.log("[msw] mock service worker active");
}

// In-browser S3 facade (blobS3Worker): serves /_s3/<bucket>/<key> from OPFS.
// Prod only — in dev, MSW's worker owns the SW scope (one SW per scope), so
// the facade isn't mounted there. Best-effort; the app works without it.
function maybeRegisterS3Worker(): void {
  if (import.meta.env.DEV) return;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
    return;
  // Built as a dedicated entry to a stable path (vite.sw.config.ts); register
  // by URL. (Vite doesn't compile a serviceWorker.register(new URL(...).ts).)
  void navigator.serviceWorker
    .register("/blob-s3-sw.js", { type: "module" })
    .catch((e) => console.warn("[s3sw] registration failed", e));
}

// The preview service worker (src/preview/previewSw.ts): serves /_preview/.
// Its own, narrower scope, so it coexists with MSW (dev) and blob-s3-sw
// (prod) — each document gets the most specific scope's worker. Registered
// at startup so it is active before the first Preview click; the /_preview/
// boot page covers the case where it isn't.
function registerPreviewWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
    return;
  void navigator.serviceWorker
    .register(PREVIEW_SW_URL, { scope: PREVIEW_ROOT, type: "module" })
    .catch((e) => console.warn("[preview] registration failed", e));
}

void maybeStartMsw().then(() => {
  maybeRegisterS3Worker();
  registerPreviewWorker();
  const rootEl = document.getElementById("root");
  if (rootEl) {
    createRoot(rootEl).render(
      <StrictMode>
        <ThemeProvider theme="light">
          {/* The editor canvas renders its widgets in Duro. */}
          <DesignSystemScope ds={duroModule}>
            <RouterProvider router={router} />
          </DesignSystemScope>
        </ThemeProvider>
      </StrictMode>,
    );
  }
});
